// client/src/pages/PasswordReset/index.jsx
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import customFetch from '../../api/customFetch';
import {
  PageContainer,
  FormCard,
  FormHeader,
  IconWrapper,
  Title,
  Subtitle,
  Form,
  FormGroup,
  Label,
  Input,
  SubmitButton,
  BackLink,
  Alert,
  AlertIcon,
  StatusCard,
  StatusIcon,
  StatusTitle,
  StatusMessage,
  ActionButton,
  Spinner,
  PasswordStrength,
  StrengthBar,
  StrengthText,
} from './styles';

export default function PasswordReset() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tokenValid, setTokenValid] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('חסר טוקן איפוס. אנא בקש לינק חדש מהמנהל.');
      setTokenValid(false);
      return;
    }
    setTokenValid(true);
  }, [token]);

  const checkPasswordStrength = (password) => {
    if (!password) return null;
    if (password.length < 6) return 'weak';
    if (
      password.length < 8 ||
      !/[A-Z]/.test(password) ||
      !/[0-9]/.test(password)
    )
      return 'medium';
    return 'strong';
  };

  const getStrengthText = (strength) => {
    switch (strength) {
      case 'weak':
        return 'סיסמה חלשה';
      case 'medium':
        return 'סיסמה בינונית';
      case 'strong':
        return 'סיסמה חזקה';
      default:
        return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'newPassword') {
      setPasswordStrength(checkPasswordStrength(value));
    }

    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.newPassword) {
      setError('אנא הזן סיסמה חדשה');
      return false;
    }

    if (formData.newPassword.length < 6) {
      setError('הסיסמה חייבת להכיל לפחות 6 תווים');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('הסיסמאות אינן זהות');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await customFetch.post('/users/reset-password', {
        token: token,
        newPassword: formData.newPassword,
      });

      if (response.data.success) {
        setSuccess('הסיסמה שונתה בהצלחה! אתה מועבר לדף ההתחברות...');

        setTimeout(() => {
          navigate('/user/login');
        }, 3000);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setError(error.response.data.message || 'הטוקן לא תקין או פג תוקף');
      } else {
        setError('אירעה שגיאה באיפוס הסיסמה. אנא נסה שוב.');
      }
    } finally {
      setLoading(false);
    }
  };

  // מצב שגיאה - טוקן לא תקין
  if (tokenValid === false) {
    return (
      <PageContainer>
        <StatusCard>
          <StatusIcon type='error'>❌</StatusIcon>
          <StatusTitle type='error'>שגיאה</StatusTitle>
          <StatusMessage>{error}</StatusMessage>
          <ActionButton
            variant='primary'
            onClick={() => navigate('/user/login')}
          >
            חזור לדף התחברות
          </ActionButton>
        </StatusCard>
      </PageContainer>
    );
  }

  // מצב הצלחה
  if (success) {
    return (
      <PageContainer>
        <StatusCard>
          <StatusIcon type='success'>✅</StatusIcon>
          <StatusTitle type='success'>הצלחה!</StatusTitle>
          <StatusMessage>{success}</StatusMessage>
        </StatusCard>
      </PageContainer>
    );
  }

  // טופס איפוס סיסמה
  return (
    <PageContainer>
      <FormCard>
        <FormHeader>
          <IconWrapper>🔐</IconWrapper>
          <Title>איפוס סיסמה</Title>
          <Subtitle>אנא הזן סיסמה חדשה לחשבון שלך</Subtitle>
        </FormHeader>

        {error && (
          <Alert type='error'>
            <AlertIcon>⚠️</AlertIcon>
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>סיסמה חדשה</Label>
            <Input
              type='password'
              name='newPassword'
              value={formData.newPassword}
              onChange={handleInputChange}
              placeholder='הזן סיסמה חדשה...'
              required
              disabled={loading}
            />
            {passwordStrength && (
              <>
                <PasswordStrength>
                  <StrengthBar active={true} strength={passwordStrength} />
                  <StrengthBar
                    active={passwordStrength !== 'weak'}
                    strength={passwordStrength}
                  />
                  <StrengthBar
                    active={passwordStrength === 'strong'}
                    strength={passwordStrength}
                  />
                </PasswordStrength>
                <StrengthText strength={passwordStrength}>
                  {getStrengthText(passwordStrength)}
                </StrengthText>
              </>
            )}
          </FormGroup>

          <FormGroup>
            <Label>אישור סיסמה</Label>
            <Input
              type='password'
              name='confirmPassword'
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder='הזן שוב את הסיסמה...'
              required
              disabled={loading}
            />
          </FormGroup>

          <SubmitButton type='submit' disabled={loading}>
            {loading ? (
              <>
                <Spinner />
                מעדכן סיסמה...
              </>
            ) : (
              'עדכן סיסמה'
            )}
          </SubmitButton>
        </Form>

        <BackLink type='button' onClick={() => navigate('/user/login')}>
          חזור לדף התחברות
        </BackLink>
      </FormCard>
    </PageContainer>
  );
}
